import type React from "react";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { CheckIcon, TriangleIcon } from "@icons";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import pickerStyles from "./ComponentAnatomySchematicPicker.module.scss";

/** One resolved-binding reader, shared by the picker plate and its rows. */
export type PickerBindingReader = ReturnType<typeof bindingReader>;

/** Inline text style for one of the picker's text-bearing parts. */
export const pickerTextStyle = (
    read: PickerBindingReader,
    partId: "label" | "itemLabel" | "sectionTitle",
) => ({
    fontFamily: read(partId, "font-family"),
    fontSize: read(partId, "font-size"),
    fontWeight: read(partId, "font-weight"),
    lineHeight: read(partId, "line-height"),
    letterSpacing: read(partId, "letter-spacing"),
});

/** One popup row on the x-ray plate: connector, label, expand glyph, indicator. */
export const PickerSchematicRow = ({
    read,
    highlight,
    label,
    isStateRow,
    isSelected,
    variantId,
    depth = 0,
    treePosition,
    hasExpandToggle = false,
}: {
    read: PickerBindingReader;
    highlight: AnatomyPartHighlight;
    label: string;
    isStateRow: boolean;
    isSelected: boolean;
    variantId: string;
    depth?: number;
    treePosition?: "top" | "middle" | "bottom" | "only";
    hasExpandToggle?: boolean;
}) => {
    const indicatorColor = read("indicator", "color");
    const indicatorSize = read("indicator", "width");
    const connectorWidth = read("connector", "width");
    const connectorLeft =
        depth > 0 ? `calc(16px + ${depth - 1} * 24px)` : undefined;
    // Mirrors .treeConnector: 8px inset at a group's start and at its end
    // (the label baseline, 20px from the top of a 28px row), and a 1px
    // overhang (half the 2px row gap) between siblings.
    let connectorInset: React.CSSProperties = {};
    if (treePosition === "top") {
        connectorInset = { top: 8, bottom: -1 };
    } else if (treePosition === "middle") {
        connectorInset = { top: -1, bottom: -1 };
    } else if (treePosition === "bottom") {
        connectorInset = { top: -1, bottom: 8 };
    } else if (treePosition === "only") {
        connectorInset = { top: 8, bottom: 8 };
    }

    return (
        <Part
            partId="itemBackground"
            highlight={highlight}
            className={pickerStyles.pickerRow}
            style={{
                minHeight: read("itemBackground", "min-height"),
                paddingInline: read("itemBackground", "padding-inline"),
                paddingLeft:
                    depth > 0 ? `calc(8px + ${depth} * 24px)` : undefined,
                gap: read("itemBackground", "gap"),
                borderRadius: read("itemBackground", "border-radius"),
                backgroundColor: isStateRow
                    ? read("itemBackground", "background-color")
                    : "transparent",
            }}
        >
            {treePosition !== undefined && (
                <Part
                    partId="connector"
                    highlight={highlight}
                    className={pickerStyles.pickerConnector}
                    style={{
                        left: connectorLeft,
                        width: connectorWidth,
                        backgroundColor: read("connector", "background-color"),
                        ...connectorInset,
                    }}
                />
            )}
            <Part
                partId="itemLabel"
                highlight={highlight}
                className={pickerStyles.pickerRowLabel}
                style={{
                    color: isStateRow
                        ? read("itemLabel", "color")
                        : "var(--fg-primary)",
                    ...pickerTextStyle(read, "itemLabel"),
                }}
            >
                {label}
            </Part>
            {hasExpandToggle && (
                <span className={pickerStyles.pickerExpandToggle}>
                    <TriangleIcon />
                </span>
            )}
            {(isSelected ||
                variantId === "multiple" ||
                variantId === "flyout") && (
                <Part
                    partId="indicator"
                    highlight={highlight}
                    className={pickerStyles.pickerIndicator}
                    style={{
                        width: indicatorSize,
                        height: indicatorSize,
                        color: isStateRow
                            ? indicatorColor
                            : "var(--fg-primary)",
                    }}
                >
                    {variantId === "multiple" && (
                        <CheckboxIndicator
                            state={isSelected ? "checked" : "unchecked"}
                            disabled={
                                isStateRow &&
                                indicatorColor === "var(--fg-disabled)"
                            }
                        />
                    )}
                    {variantId === "flyout" && (
                        <TriangleIcon
                            className={pickerStyles.pickerFlyoutIndicator}
                        />
                    )}
                    {variantId !== "multiple" && variantId !== "flyout" && (
                        <CheckIcon />
                    )}
                </Part>
            )}
        </Part>
    );
};
